/**
 * Lead value + potential providers helpers.
 * Pure functions for the decluttered admin lead decision strip.
 */

import {
  formatLeadQuoteValue,
  getLeadEngine,
  getLeadProviderStatus,
  getLeadQuoteValue,
  getLeadTruckRecommendation,
} from "./leadOperationalStatus.js";
import {
  formatTruckLabel,
  getLeadBillableVolumeM3,
  getLeadCustomerConfidence,
  getLeadDistanceKm,
  getLeadJobSummary,
  getLeadOpsPanelRows,
  getLeadRouteLabel,
} from "./leadJobCockpit.js";

const ZAR = new Intl.NumberFormat("en-ZA", {
  style: "currency",
  currency: "ZAR",
  maximumFractionDigits: 0,
});

/**
 * @param {number|null|undefined} amount
 * @returns {string|null}
 */
export function formatProviderAmount(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n) || n <= 0) return null;
  return ZAR.format(n).replace(/\u00a0/g, " ");
}

/**
 * Normalize a bid document into a display row.
 * @param {Object} bid
 * @returns {Object}
 */
export function mapBidToProviderRow(bid) {
  const amount = Number(bid?.basePrice ?? bid?.amount ?? bid?.price);
  const name =
    bid?.submitterName ||
    bid?.partnerName ||
    bid?.partner?.name ||
    bid?.submitterPhone ||
    "Provider";
  const status = String(bid?.status || "pending").toLowerCase();
  let statusKey = "bid_open";
  let statusLabel = "Bid open";
  if (status === "accepted" || status === "selected" || status === "won") {
    statusKey = "selected";
    statusLabel = "Selected";
  } else if (status === "rejected" || status === "declined") {
    statusKey = "rejected";
    statusLabel = "Rejected";
  } else if (status === "withdrawn") {
    statusKey = "withdrawn";
    statusLabel = "Withdrawn";
  } else if (bid?.source === "admin_manual") {
    statusKey = "manual";
    statusLabel = "Manual bid";
  }

  return {
    id: String(bid?.id || bid?._id || `${name}-${amount}`),
    name,
    statusKey,
    statusLabel,
    amount: Number.isFinite(amount) && amount > 0 ? amount : null,
    amountLabel: formatProviderAmount(amount),
    amountPrefix: Number.isFinite(amount) && amount > 0 ? "Offered" : null,
    truckTypes: bid?.truckType || bid?.truckTypes || "",
    partnerId: bid?.partnerId || null,
    source: "bid",
  };
}

/**
 * Build potential-provider rows from bids + marketplace state.
 * @param {Object} lead
 * @param {Array} [bids]
 * @param {{ maxRows?: number }} [options]
 */
export function buildPotentialProviderRows(lead, bids = [], options = {}) {
  const maxRows = options.maxRows ?? 3;
  const list = Array.isArray(bids) ? bids : [];
  const mapped = list
    .map(mapBidToProviderRow)
    .sort((a, b) => {
      if (a.statusKey === "selected" && b.statusKey !== "selected") return -1;
      if (b.statusKey === "selected" && a.statusKey !== "selected") return 1;
      if (a.amount != null && b.amount != null) return a.amount - b.amount;
      if (a.amount != null) return -1;
      if (b.amount != null) return 1;
      return String(a.name).localeCompare(String(b.name));
    });

  const providerStatus = getLeadProviderStatus(lead);
  const biddingStatus = String(lead?.bidding?.status || "none").toLowerCase();
  let marketplaceLabel = null;
  if (biddingStatus === "open") {
    marketplaceLabel = "Marketplace open";
  } else if (providerStatus?.label) {
    marketplaceLabel = providerStatus.label;
  }

  let emptyMessage = "No providers yet — invite to bid or assign directly.";
  if (biddingStatus === "open" && mapped.length === 0) {
    emptyMessage = "Marketplace open — waiting for provider bids.";
  } else if (biddingStatus === "closed" && mapped.length === 0) {
    emptyMessage = "Bidding closed — no bids recorded.";
  }

  return {
    rows: mapped.slice(0, maxRows),
    totalCount: mapped.length,
    moreCount: Math.max(0, mapped.length - maxRows),
    marketplaceLabel,
    emptyMessage,
  };
}

/**
 * Decision-strip model: route meta + single customer price + confidence.
 * @param {Object} lead
 * @param {Object|null|undefined} [quote]
 */
export function getLeadValueStripModel(lead, quote = null) {
  const summary = getLeadJobSummary(lead, quote);
  const confidence = getLeadCustomerConfidence(lead, quote);
  const distanceKm = getLeadDistanceKm(lead, quote);
  const price = getLeadQuoteValue(lead, quote);
  const engineLabel =
    summary?.engine?.label || getLeadEngine(lead)?.label || null;
  const truck =
    summary?.truck || formatTruckLabel(getLeadTruckRecommendation(lead));
  const moveDate = lead?.move_date || lead?.moveDate || lead?.date || null;
  const metaParts = [
    engineLabel,
    moveDate,
    truck && truck !== "—" ? truck : null,
    distanceKm != null ? `${Math.round(distanceKm)} km` : null,
  ].filter(Boolean);

  const confidencePct = confidence?.quoteConfidencePct ?? null;
  let confidenceLabel = null;
  if (confidencePct != null) {
    confidenceLabel = `Quote confidence ${confidencePct}%`;
  } else if (
    confidence?.quoteConfidenceLabel &&
    confidence.quoteConfidenceLabel !== "—"
  ) {
    confidenceLabel = `Quote confidence ${confidence.quoteConfidenceLabel}`;
  }

  return {
    routeLabel: summary?.routeLabel || getLeadRouteLabel(lead),
    metaLine: metaParts.join(" · "),
    price,
    priceLabel: summary?.priceLabel || formatLeadQuoteValue(price),
    confidencePct,
    confidenceLabel,
    providerStatus: summary?.provider || getLeadProviderStatus(lead),
  };
}

/**
 * Compact facts row model (engine / truck / volume / access).
 * @param {Object} lead
 * @param {Object|null|undefined} [quote]
 */
export function getLeadFactsRowModel(lead, quote = null) {
  const summary = getLeadJobSummary(lead, quote);
  const opsRows = getLeadOpsPanelRows(lead, quote);
  const byKey = Object.fromEntries(
    (opsRows || []).map((row) => [row.key, row.value])
  );
  const volumeM3 = getLeadBillableVolumeM3(lead, quote);

  return [
    {
      key: "engine",
      label: "Engine",
      value: summary?.engine?.label || "—",
    },
    {
      key: "truck",
      label: "Truck",
      value: summary?.truck || byKey.truck || "—",
    },
    {
      key: "volume",
      label: "Volume",
      value:
        summary?.volumeLabel ||
        (volumeM3 != null ? `${Number(volumeM3).toFixed(2)} m³` : "—"),
    },
    {
      key: "access",
      label: "Access",
      value: byKey.access || "None noted",
    },
  ];
}
